import {
  Component,
  computed,
  effect,
  inject,
  model,
  OnInit,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {NgTemplateOutlet} from '@angular/common';
import {Editor} from 'primeng/editor';
import {FormsModule} from '@angular/forms';
import {Button} from 'primeng/button';
import {Tooltip} from 'primeng/tooltip';
import {DirectusService, TextTemplate, TextTemplateTranslation} from '@frontend/directus';
import {firstValueFrom} from 'rxjs';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {TranslateDirectusPipe} from '@frontend/transloco';
import {DirectusTooltipComponent} from '../directus-tooltip/directus-tooltip.component';
import {ConfirmStepLeaveDirective} from '../../project-step-save-button/confirm-step-leave.directive';
import {COLORS, QuillModulesConfig} from './rich-text-editor.types';
import {RichTextEditorHandler} from '../../services/project-step-rich-text.service';
import {ConfirmationDialogService} from '../dialog/confirmation/confirmation-dialog.service';

let editorInstanceCounter = 0;

@Component({
  selector: 'app-rich-text-editor',
  templateUrl: './rich-text-editor.component.html',
  styleUrl: './rich-text-editor.component.scss',
  imports: [
    FormsModule,
    NgTemplateOutlet,
    Editor,
    Button,
    Tooltip,
    TranslocoPipe,
    TranslateDirectusPipe,
    DirectusTooltipComponent,
    ConfirmStepLeaveDirective,
  ],
})
export class RichTextEditorComponent implements OnInit {
  private readonly directusService = inject(DirectusService);
  private readonly confirmDialogService = inject(ConfirmationDialogService);
  private readonly translocoService = inject(TranslocoService);

  readonly key = input.required<string>();
  readonly showSaveButton = input<boolean | null>(null);
  readonly tooltipKey = input<string | null>(null);
  readonly saveHandler = input<RichTextEditorHandler | null>(null);
  readonly labelPrefix = input('');
  readonly customTranslationName = model('');
  readonly showLabel = input(true);
  readonly disabled = input(false);
  readonly overrideLanguage = input<string | null>(null);
  readonly saveContent = output<string>();
  readonly contentChange = output<string>();
  readonly contentReset = output<{
    content: string;
    templateName: string;
  }>();
  readonly statusChange = output<void>();
  readonly templateLoaded = output<string>();
  readonly editor = viewChild<Editor>('editor');

  readonly translations = signal<TextTemplateTranslation[]>([]);
  readonly content = signal('');
  readonly originalContent = signal('');
  readonly translationName = signal('');
  readonly isLoading = signal(false);
  readonly isSaving = signal(false);
  private readonly contentLoaded = signal(false);
  private readonly hasUnsavedChanges = signal<boolean>(false);

  private readonly activeLang = toSignal(this.translocoService.langChanges$, {
    initialValue: this.translocoService.getActiveLang(),
  });

  private readonly effectiveLanguage = computed<string>(
    () => this.overrideLanguage() || this.activeLang(),
  );

  protected readonly resolvedLanguage = computed<string>(() =>
    this.resolveLanguage(this.effectiveLanguage()),
  );

  private appliedOverrideLanguage: string | null = null;
  private readonly originalContentByLang = new Map<string, string>();
  private readonly translationNameByLang = new Map<string, string>();
  readonly colors = COLORS;
  readonly containerId = `rich-text-editor-${++editorInstanceCounter}`;

  readonly initialContent = input<string | null>(null);

  readonly editorModules = computed<QuillModulesConfig>(() => ({
    toolbar: {
      container: `#${this.containerId} .p-editor-toolbar`,
      handlers: {
        undo: () => this.undo(),
        redo: () => this.redo(),
      },
    },
    history: {
      delay: 2000,
      maxStack: 500,
      userOnly: true,
    },
  }));

  readonly currentLabel = computed<string>(
    () => this.customTranslationName() || this.translationName(),
  );

  readonly isLabelVisible = computed<boolean>(() => this.showLabel() && !!this.currentLabel());

  readonly isReady = computed<boolean>(
    () => !!this.key() && !this.isLoading() && this.contentLoaded(),
  );

  readonly isRichTextContentNotBlank = computed<boolean>(() => {
    const doc = new DOMParser().parseFromString(this.content() || '', 'text/html');

    const text = doc.body.textContent || '';

    return text.trim().length > 0;
  });

  private readonly hasSavedContent = computed<boolean>(() => (this.initialContent() ?? '') !== '');

  private readonly isDirty = computed<boolean>(
    () => this.hasUnsavedChanges() || !this.hasSavedContent(),
  );

  readonly shouldDisplaySaveButton = computed<boolean>(() => {
    if (this.disabled()) {
      return false;
    }

    const showSaveButton = this.showSaveButton();
    if (showSaveButton !== null) {
      return showSaveButton;
    }

    return this.isRichTextContentNotBlank() && this.isDirty();
  });

  readonly isSaveDisabled = computed<boolean>(() => this.isLoading() || this.isSaving());

  constructor() {
    effect(() => {
      const value = this.initialContent();
      if (value != null) {
        untracked(() => this.handleInitialContent(value));
      }
    });

    effect(() => {
      this.applyOverrideLanguage();
    });
  }

  ngOnInit(): void {
    this.loadContentFromDirectus();
  }

  private handleInitialContent(content: string): void {
    const incoming = this.cleanHtmlContent(content);

    if (incoming !== this.cleanHtmlContent(this.content())) {
      this.content.set(content);
    }

    this.contentLoaded.set(true);
  }

  async loadContentFromDirectus(): Promise<void> {
    if (!this.key()) {
      return;
    }

    this.isLoading.set(true);
    try {
      const response: TextTemplate | null = await firstValueFrom(
        this.directusService.loadTextTemplate(this.key()),
      );
      this.translations.set(response?.translations ?? []);

      response?.translations?.forEach((translation) => {
        const lang = translation.language;
        const template = translation.template ?? '';
        const name = translation.name ?? '';

        this.originalContentByLang.set(lang, template);
        this.translationNameByLang.set(lang, name);
      });

      const currentLang = this.resolvedLanguage();

      this.originalContent.set(this.originalContentByLang.get(currentLang) ?? '');
      this.translationName.set(this.translationNameByLang.get(currentLang) ?? '');
      this.appliedOverrideLanguage = this.overrideLanguage();

      if (!this.initialContent()) {
        this.content.set(this.originalContent());
      }

      // Emit the template name for parent components
      if (this.translationName()) {
        this.templateLoaded.emit(this.translationName());
      }

      this.contentLoaded.set(true);
    } finally {
      this.isLoading.set(false);
    }
  }

  undo(): void {
    this.editor()?.quill?.history.undo();
  }

  redo(): void {
    this.editor()?.quill?.history.redo();
  }

  async saveTemplate(): Promise<void> {
    const cleanedContent = this.cleanHtmlContent(this.content());
    const projectStepService = this.saveHandler();
    if (projectStepService) {
      this.isSaving.set(true);
      try {
        await firstValueFrom(projectStepService.onSave(this.key(), cleanedContent));
        this.hasUnsavedChanges.set(false);
        this.statusChange.emit();
      } finally {
        this.isSaving.set(false);
      }
    } else {
      this.saveContent.emit(cleanedContent);
      this.hasUnsavedChanges.set(false);
    }
  }

  confirmReset(): void {
    this.confirmDialogService.confirm({
      headerKey: 'textEditor.reset.tooltip',
      messageKey: 'textEditor.reset.confirmationDialog',
      accept: () => {
        this.resetContent();
      },
    });
  }

  resetContent(): void {
    const quill = this.editor()?.quill;
    if (quill) {
      // Get content for the current effective language
      const currentLang = this.effectiveLanguage();
      const langContent = this.originalContentByLang.get(currentLang);
      const langName = this.translationNameByLang.get(currentLang);

      // Use language-specific content if available, otherwise use current content
      this.content.set(langContent || this.originalContent());
      this.translationName.set(langName || this.translationName());
      this.customTranslationName.set('');
      this.hasUnsavedChanges.set(true);
      quill.history.clear();
      this.onContentResetInternal();
    }
  }

  onContentChange(value: string): void {
    this.content.set(value);
    this.hasUnsavedChanges.set(true);

    this.contentChange.emit(this.cleanHtmlContent(value));
    if (this.saveHandler()) {
      this.handleProjectStepContentChange();
    }
  }

  private applyOverrideLanguage(): void {
    const override = this.overrideLanguage();
    const hasTranslations = this.translations().length > 0;

    if (!override || !hasTranslations || override === this.appliedOverrideLanguage) {
      return;
    }
    const template = this.applyTemplate(override);
    this.contentChange.emit(this.cleanHtmlContent(template));
  }

  restoreTemplate(language: string): void {
    this.applyTemplate(language);
    this.customTranslationName.set('');
    this.editor()?.quill?.history.clear();
  }

  private applyTemplate(preferredLanguage: string): string {
    this.appliedOverrideLanguage = preferredLanguage;

    const language = this.resolveLanguage(preferredLanguage);
    const template = this.originalContentByLang.get(language) ?? '';

    this.originalContent.set(template);
    this.translationName.set(this.translationNameByLang.get(language) ?? '');
    this.content.set(template);
    this.hasUnsavedChanges.set(true);
    this.templateLoaded.emit(this.translationName());
    return template;
  }

  private resolveLanguage(preferredLanguage: string): string {
    const translations = this.translations();

    return translations.some((translation) => translation.language === preferredLanguage)
      ? preferredLanguage
      : (translations[0]?.language ?? preferredLanguage);
  }

  private handleProjectStepContentChange(): void {
    const projectStepService = this.saveHandler();
    if (projectStepService) {
      projectStepService.onContentChange(this.key(), this.content());
    }
  }

  onContentResetInternal(): void {
    const cleanedContent = this.cleanHtmlContent(this.content());
    this.contentReset.emit({
      content: cleanedContent,
      templateName: this.translationName(),
    });
    const projectStepService = this.saveHandler();
    if (projectStepService) {
      projectStepService.onContentReset(this.key(), cleanedContent);
    }
  }

  private cleanHtmlContent(content: string): string {
    return content.replace(/&nbsp;/g, ' ');
  }
}
